import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { FeeDialog } from 'pages/Loan/ClosingCalendar/FeeDialog'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getInvestorServicersItem, submitInvestorServicer } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button, PlainTable } from 'stories/components'
import { InputConvert, InputValidate, thousandSeperatorWithPrefix } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { InvestorServicer } from '.'
import { defaultInputs } from './constants'

interface InvestorsServicerDetailsProps {
  defaults: InvestorServicer | null
  onBack: Function
  onComplete: Function
}

export function InvestorsServicerDetails(props: InvestorsServicerDetailsProps) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [isBrokerModalOpen, setIsBrokerModalOpen] = useState<boolean>(false)
  const [feeData, setFeeData] = useState<any>([])
  const { defaults = null } = props

  useEffect(() => {
    onUpdateDefaults()
  }, [defaults])

  const onUpdateDefaults = async () => {
    if (!defaults) {
      setInputs(defaultInputs())
      return
    }
    const newStats = cloneDeep(defaultInputs())
    setLoading(true)
    const item: InvestorServicer = await getInvestorServicersItem(defaults.id)
    setLoading(false)
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key] || item.data![key]
    }
    setInputs(newStats)
    setFeeData(item.data?.fees || [])
  }

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    if (hasError) {
      setInputs(newStats)
      return
    }
    setLoading(true)
    const id = defaults ? defaults.id : 0

    const { type, investorCode, fullName, ...otherData } = data
    otherData.fees = feeData

    submitInvestorServicer(id, {
      type,
      investorCode,
      fullName,
      data: otherData,
    })
      .then(() => {
        if (id == 0) toast('New InvestorServicer is added.', { type: 'success' })
        else toast(`InvestorServicer Code ${defaults?.investorCode} is updated.`, { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const onFeeSubmit = (value: any) => {
    setFeeData(value)
  }

  const onClose = () => {
    setIsBrokerModalOpen(false)
  }

  return (
    <div className="investor-servicer-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Investor & Servicer - {!defaults ? 'Add' : `Update Code ${defaults.investorCode}`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Investors & Servicers</p>
        </div>
      </Button>

      <div className="grid gap-4 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]

          return (
            <div
              className={`input ${input.span ? 'col-span-1 lg:col-span-4 md:col-span-3 sm:col-span-2' : ''}`}
              key={index}
            >
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        <div className="relative overflow-auto">
          <span className="flex justify-between">
            Fees <PencilSquareIcon className="w-4 h-4 cursor-pointer" onClick={() => setIsBrokerModalOpen(true)} />
          </span>
          <PlainTable
            header={['No', 'Title', 'Amount']}
            tdClass="px-6 py-4 whitespace-nowrap capitalize"
            data={feeData.map((item: any, index: number) => [
              index + 1,
              item.title,
              thousandSeperatorWithPrefix(item.amount),
            ])}
          />
        </div>
      </div>

      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'}</>
        </Button>
      </div>
      {isBrokerModalOpen && (
        <FeeDialog modalTitle="Update Fees" onClose={onClose} onSubmit={onFeeSubmit} data={feeData} />
      )}
    </div>
  )
}
